import { isEqual } from "date-fns";


/**
 * Performs a deep equality comparison between two values, supporting primitives, arrays, dates, and objects.
 * @param obj1 First value to compare.
 * @param obj2 Second value to compare against the first.
 * @returns True when both values are structurally equivalent; otherwise false.
 */
export function deepEqual(obj1: any, obj2: any): boolean {

    if (obj1 === undefined && obj2 === undefined)
        return true;

    if (obj1 === undefined && obj2 !== undefined)
        return false;

    if (obj1 !== undefined && obj2 === undefined)
        return false;

    if (obj1 === null && obj2 === null)
        return true;

    if (obj1 === null && obj2 !== null)
        return false;

    if (obj1 !== null && obj2 === null)
        return false;

    if (obj1 === obj2) // it's just the same object. No need to compare.
        return true;

    //if arrays
    if (Array.isArray(obj1) && Array.isArray(obj2)) {
        if (obj1.length !== obj2.length) {
            return false;
        }
        for (let i = 0; i < obj1.length; i++) {
            const item1 = obj1[i];
            const item2 = obj2[i];
            let isEqual = deepEqual(item1, item2);
            if (!isEqual)
                return false;
        }
        return true;
    }

    if (obj1 instanceof Date && obj2 instanceof Date) {
        return isEqual(obj1, obj2);
    }

    if (isPrimitive(obj1) && isPrimitive(obj2)) // compare primitives
        return obj1 === obj2;

    if (Object.keys(obj1).length !== Object.keys(obj2).length)
        return false;

    // compare objects with same number of keys
    for (let key in obj1) {
        if (!(key in obj2)) return false; //other object doesn't have this prop
        if (!deepEqual(obj1[key], obj2[key])) return false;
    }

    return true;
}

/**
 * Checks whether a value is a primitive (non-object) type.
 * @param obj Value to test.
 * @returns True when the value is primitive; otherwise false.
 */
function isPrimitive(obj: any): boolean {
    return (obj !== Object(obj));
}


/**
 * Determines whether an object's enumerable values are empty, null, or undefined.
 * @param obj Object to inspect.
 * @returns True when all values are nullish or empty strings.
 */
export const isObjEmpty = (obj: object): boolean => {
    if (obj === undefined || obj === null)
        return true;
    let values = Object.values(obj);
    for (let i = 0; i < values.length; i++) {
        const value = values[i];
        if (value !== null && value !== undefined && value !== '') {
            return false;
        }
    }
    return true;
}

/**
 * Flattens nested objects into a single-level dictionary with underscored keys up to a depth of three.
 * @param obj Source object to flatten.
 * @param prefix Prefix applied to generated keys (used internally during recursion).
 * @param currentDepth Current recursion depth; defaults to 1.
 * @returns Flattened object containing primitive values or JSON strings for nested objects beyond the depth limit.
 */
export function flattenObject(obj: any, prefix: string = '', currentDepth: number = 1): any {
    let flattened: any = {};

    for (let i in obj) {
        if ((typeof obj[i]) === 'object' && obj[i] !== null && currentDepth <= 3) {
            let nextPrefix = prefix ? `${prefix}_${i}` : i;
            let nextDepth = currentDepth + 1;
            let flatObject = flattenObject(obj[i], nextPrefix, nextDepth);
            flattened = { ...flattened, ...flatObject };
        } else {
            let key = prefix ? `${prefix}_${i}` : i;
            if ((typeof obj[i]) === 'object' && obj[i] !== null) {
                flattened[key + '_JSON'] = JSON.stringify(obj[i]);
            } else {
                flattened[key] = obj[i];
            }
        }
    }

    return flattened;
}

/**
 * Attempts to read a file to determine whether it can be opened without errors.
 * @param file File instance to validate.
 * @returns Promise resolving to true when the file is readable, false otherwise.
 */
export const validateFileOpen = async (file: File) => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => {
                // The file was read successfully.
                //console.log("File read successfully");
                resolve(true);
            };
            reader.onerror = (err) => {
                // Reading failed — maybe it was locked.
                //console.error("Error reading file:", err);
                resolve(false);
            };
            console.log("Reading file:", file.name);
            reader.readAsArrayBuffer(file);
        });
    }